// Content overlay (registry editing / collections). PURE (ENG-1).
//
// An overlay is a sparse patch over the shipped `data/` registries: per entry, only
// the fields that were actually changed. Editing in game writes one of these; the
// same structure exports as an archive, drops into the repo, and merges into a
// versioned collection — so a tweak made while playing and a tweak made in the
// repo are the same object travelling in opposite directions.
//
// Sparseness is the point: storing whole entries would silently freeze every
// untouched field at whatever the shipped data said when the edit was made, so
// later upstream changes would never reach an edited entry.

/** Registry kinds an overlay may patch. Keyed by the entry's `name`. */
export const OVERLAY_KINDS = ['items', 'blocks', 'mobs', 'recipes', 'lessons'] as const;
export type OverlayKind = (typeof OVERLAY_KINDS)[number];

/** Field-level patches for one registry, keyed by entry name. */
export type KindPatch = Record<string, Record<string, unknown>>;

export interface Overlay {
  /** Which collection these edits belong to (default: the built-in one). */
  collection: string;
  /** Semver-ish version of the collection this overlay targets/produces. */
  version: string;
  patches: Partial<Record<OverlayKind, KindPatch>>;
}

export function emptyOverlay(collection = 'default', version = '0.1'): Overlay {
  return { collection, version, patches: {} };
}

/** Is there anything in here at all? */
export function isEmptyOverlay(o: Overlay): boolean {
  return Object.values(o.patches).every((k) => !k || Object.keys(k).length === 0);
}

/** How many entries this overlay touches, across every kind. */
export function overlaySize(o: Overlay): number {
  return Object.values(o.patches).reduce((n, k) => n + Object.keys(k ?? {}).length, 0);
}

/** A field set to this sentinel is REMOVED from the entry rather than assigned,
 *  which is how an optional field gets cleared back to "unset". */
export const UNSET = '__unset__';

/** Apply one entry's patch. Returns a new object; the input is never mutated. */
export function patchEntry(entry: Record<string, unknown>, patch: Record<string, unknown>): Record<string, unknown> {
  const out = { ...entry };
  for (const [k, v] of Object.entries(patch)) {
    if (v === UNSET) delete out[k];
    else out[k] = v;
  }
  return out;
}

/** Apply a kind's patches across a registry's raw entries (matched by `name`).
 *  Entries named in the patch but absent from `entries` are appended, so an
 *  overlay can add wholly new content, not only edit what shipped. */
export function applyKind(entries: readonly unknown[], patch: KindPatch | undefined): unknown[] {
  if (!patch || Object.keys(patch).length === 0) return entries as unknown[];
  const seen = new Set<string>();
  const out = entries.map((raw) => {
    const e = raw as Record<string, unknown>;
    const name = typeof e?.name === 'string' ? e.name : undefined;
    if (!name || !patch[name]) return raw;
    seen.add(name);
    return patchEntry(e, patch[name]!);
  });
  for (const [name, fields] of Object.entries(patch)) {
    if (seen.has(name)) continue;
    out.push(patchEntry({ name }, fields));
  }
  return out;
}

/** Merge `b` over `a` (b wins per field). Used to stack a collection archive
 *  under the player's own local edits. */
export function mergeOverlays(a: Overlay, b: Overlay): Overlay {
  const patches: Partial<Record<OverlayKind, KindPatch>> = {};
  for (const kind of OVERLAY_KINDS) {
    const ka = a.patches[kind], kb = b.patches[kind];
    if (!ka && !kb) continue;
    const merged: KindPatch = { ...(ka ?? {}) };
    for (const [name, fields] of Object.entries(kb ?? {})) {
      merged[name] = { ...(merged[name] ?? {}), ...fields };
    }
    patches[kind] = merged;
  }
  return { collection: b.collection || a.collection, version: b.version || a.version, patches };
}

/** Drop a single entry's patch (an "revert to shipped" in the editor). */
export function clearEntry(o: Overlay, kind: OverlayKind, name: string): Overlay {
  const kindPatch = { ...(o.patches[kind] ?? {}) };
  delete kindPatch[name];
  return { ...o, patches: { ...o.patches, [kind]: kindPatch } };
}

/** Record a field change, dropping it again if it matches the shipped value. */
export function setField(
  o: Overlay, kind: OverlayKind, name: string, field: string, value: unknown, shipped: unknown,
): Overlay {
  const kindPatch = { ...(o.patches[kind] ?? {}) };
  const entry = { ...(kindPatch[name] ?? {}) };
  const same = JSON.stringify(value) === JSON.stringify(shipped);
  if (same) delete entry[field];
  else entry[field] = value;
  if (Object.keys(entry).length === 0) delete kindPatch[name];
  else kindPatch[name] = entry;
  return { ...o, patches: { ...o.patches, [kind]: kindPatch } };
}
